import React, { useState } from 'react';
import { TrendingUp, Award, Target, Calendar, PieChart, Sparkles, Loader2, Bot } from 'lucide-react';
import { Card } from './ui/card';
import { Progress } from './ui/progress';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import InsightsCard from './InsightsCard';
import { supabase } from '../lib/supabaseClient';
import { getRandomInsight } from '../lib/insightTemplates';

const StatsView = ({ habits, completions }) => {
  const [timeRange, setTimeRange] = useState('all'); // 'all', 'month'
  const [aiInsights, setAiInsights] = useState(null);
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [isAiOpen, setIsAiOpen] = useState(false);

  const handleGenerateInsights = async () => {
    setIsAiLoading(true);
    setIsAiOpen(true);

    // Simula um tempo de "pensamento" da IA para melhor UX
    setTimeout(() => {
      try {
        const overallRate = getOverallCompletionRate();
        const insight = getRandomInsight(overallRate);
        setAiInsights(insight);
      } catch (error) {
        console.error("Erro ao gerar insight:", error);
        setAiInsights("Não foi possível gerar um insight agora. Continue seus hábitos!");
      } finally {
        setIsAiLoading(false);
      }
    }, 1500);
  };

  const getCurrentStreak = (habitId) => {
    let streak = 0;
    const date = new Date();

    while (true) {
      const dateStr = date.toISOString().split('T')[0];
      const completed = completions.some(c => c.habitId === habitId && c.date === dateStr);

      if (completed) {
        streak++;
        date.setDate(date.getDate() - 1);
      } else {
        break;
      }
    }

    return streak;
  };

  const getLongestStreak = (habitId) => {
    const habitCompletions = completions
      .filter(c => c.habitId === habitId)
      .map(c => new Date(c.date))
      .sort((a, b) => a - b);

    if (habitCompletions.length === 0) return 0;

    let maxStreak = 1;
    let currentStreak = 1;

    for (let i = 1; i < habitCompletions.length; i++) {
      const diffDays = Math.floor(
        (habitCompletions[i] - habitCompletions[i - 1]) / (1000 * 60 * 60 * 24)
      );

      if (diffDays === 1) {
        currentStreak++;
        maxStreak = Math.max(maxStreak, currentStreak);
      } else {
        currentStreak = 1;
      }
    }

    return maxStreak;
  };



  const getCompletionRate = (habitId) => {
    const habit = habits.find(h => h.id === habitId);
    if (!habit) return 0;

    const creationTime = habit.created_at || habit.createdAt;
    if (!creationTime) return 0;

    const createdDate = new Date(creationTime);
    createdDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let startDate = createdDate;
    if (timeRange === 'month') {
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      if (firstDayOfMonth > createdDate) {
        startDate = firstDayOfMonth;
      }
    }

    if (startDate > today) return 0;

    const daysInRange = Math.floor((today - startDate) / (1000 * 60 * 60 * 24)) + 1;
    const validDays = Math.max(1, daysInRange);

    // Filter completions within range
    const rangeCompletions = completions.filter(c => {
      if (c.habitId !== habitId) return false;
      const cDate = new Date(c.date + 'T00:00:00'); // Ensure correct date parsing
      return cDate >= startDate && cDate <= today;
    });

    // Group by date to handle multiple entries/numeric values correctly
    const completionsByDate = {};
    rangeCompletions.forEach(c => {
      const dateKey = c.date;
      if (!completionsByDate[dateKey]) {
        completionsByDate[dateKey] = 0;
      }

      if (habit.target && c.value) {
        completionsByDate[dateKey] += parseFloat(c.value);
      } else {
        // Boolean habit: ensures max 1 per day regardless of multiple checks
        completionsByDate[dateKey] = 1;
      }
    });

    let totalScore = 0;
    Object.values(completionsByDate).forEach(dailyValue => {
      let dailyScore = 1;
      if (habit.target) {
        dailyScore = Math.min(dailyValue / parseFloat(habit.target), 1);
      }
      totalScore += dailyScore;
    });

    return Math.min((totalScore / validDays) * 100, 100);
  };


  const getTotalCompletions = () => {
    if (timeRange === 'month') {
      const today = new Date();
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const startStr = firstDayOfMonth.toISOString().split('T')[0];
      return completions.filter(c => c.date >= startStr).length;
    }
    return completions.length;
  };

  const getOverallCompletionRate = () => {
    if (habits.length === 0) return 0;
    const rates = habits.map(h => getCompletionRate(h.id));
    return rates.reduce((sum, rate) => sum + rate, 0) / rates.length;
  };

  if (habits.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-4">
          <Target className="w-8 h-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-medium text-foreground mb-2">Sem estatísticas ainda</h3>
        <p className="text-muted-foreground">Crie hábitos e comece a monitorar para ver seu progresso!</p>
      </div>
    );
  }


  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-4 w-full md:w-auto justify-between">
          <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">Minhas Estatísticas</h2>
          <Button
            size="sm"
            variant="outline"
            className="gap-2 border-purple-200 text-purple-700 hover:bg-purple-50 dark:border-purple-800 dark:text-purple-400 dark:hover:bg-purple-900/50"
            onClick={handleGenerateInsights}
          >
            <Sparkles className="w-4 h-4" />
            Insights IA
          </Button>
        </div>
        <Select value={timeRange} onValueChange={setTimeRange}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Período" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todo o período</SelectItem>
            <SelectItem value="month">Este Mês</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Weekly Progress Chart */}
      <Card className="p-6">
        <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-primary" />
          Progresso Semanal
        </h3>
        <div className="h-40 flex items-end justify-between gap-2">
          {(() => {
            const days = [];
            const today = new Date();
            for (let i = 6; i >= 0; i--) {
              const d = new Date(today);
              d.setDate(d.getDate() - i);
              days.push(d);
            }

            return days.map(day => {
              const dateStr = day.toISOString().split('T')[0];

              // Count unique active habits completed on this day
              const activeHabitIds = new Set(habits.map(h => h.id));
              const uniqueCompletedHabits = new Set(
                completions
                  .filter(c => c.date === dateStr && activeHabitIds.has(c.habitId))
                  .map(c => c.habitId)
              );

              const totalHabits = habits.length || 1;
              const percentage = Math.round((uniqueCompletedHabits.size / totalHabits) * 100);
              const height = percentage > 0 ? Math.max(percentage, 5) : 0;

              return (
                <div key={dateStr} className="flex flex-col items-center gap-2 flex-1 h-full justify-end">
                  <div className="relative w-full flex-1 flex items-end bg-secondary/30 rounded-t-lg overflow-hidden group">
                    <div
                      className="w-full bg-primary/80 group-hover:bg-primary transition-all duration-500 rounded-t-sm relative"
                      style={{ height: `${height}%` }}
                    >
                      <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-popover text-popover-foreground text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap shadow-sm border z-10">
                        {percentage}%
                      </div>
                    </div>
                  </div>
                  <span className="text-[10px] text-muted-foreground uppercase font-medium">
                    {day.toLocaleDateString('pt-BR', { weekday: 'short' }).slice(0, 3)}
                  </span>
                </div>
              );
            });
          })()}
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900">
              <Target className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <h3 className="font-medium text-foreground">Total de Hábitos</h3>
          </div>
          <p className="text-3xl font-bold text-foreground">{habits.length}</p>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900">
              <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="font-medium text-foreground">Total de Check-ins</h3>
          </div>
          <p className="text-3xl font-bold text-foreground">{getTotalCompletions()}</p>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-lg bg-violet-100 dark:bg-violet-900">
              <Award className="w-5 h-5 text-violet-600 dark:text-violet-400" />
            </div>
            <h3 className="font-medium text-foreground">Taxa Geral</h3>
          </div>
          <p className="text-3xl font-bold text-foreground">
            {Math.round(getOverallCompletionRate())}%
          </p>
        </Card>
      </div>

      {/* Category Stats */}
      <Card className="p-6">
        <h3 className="font-semibold text-foreground mb-6 flex items-center gap-2">
          <PieChart className="w-5 h-5 text-primary" />
          Análise por Categoria
        </h3>

        {(() => {
          const categoryStats = habits.reduce((acc, habit) => {
            const cat = habit.category || 'Geral';
            if (!acc[cat]) {
              acc[cat] = {
                name: cat,
                count: 0,
                totalRate: 0,
                color: habit.color || '#94a3b8'
              };
            }
            acc[cat].count += 1;
            acc[cat].totalRate += getCompletionRate(habit.id);
            return acc;
          }, {});

          const categories = Object.values(categoryStats).map(cat => ({
            ...cat,
            avgRate: Math.round(cat.totalRate / cat.count),
            width: (cat.count / habits.length) * 100
          })).sort((a, b) => b.avgRate - a.avgRate);

          return (
            <div className="space-y-8">
              {/* Distribution Bar */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-muted-foreground">Distribuição</h4>
                <div className="h-4 w-full flex rounded-full overflow-hidden">
                  {categories.map((cat, i) => (
                    <div
                      key={cat.name}
                      style={{ width: `${cat.width}%`, backgroundColor: cat.color }}
                      className="h-full first:rounded-l-full last:rounded-r-full hover:opacity-80 transition-opacity relative group"
                    >
                      <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-popover text-popover-foreground text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap shadow-sm border border-border z-10 pointer-events-none">
                        {cat.name}: {cat.count} ({Math.round(cat.width)}%)
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-4 text-xs">
                  {categories.map(cat => (
                    <div key={cat.name} className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }} />
                      <span className="text-muted-foreground">{cat.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Performance by Category */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-muted-foreground">Desempenho Médio</h4>
                <div className="grid gap-4 sm:grid-cols-2">
                  {categories.map(cat => (
                    <div key={cat.name} className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="font-medium text-foreground">{cat.name}</span>
                        <span className="text-muted-foreground">{cat.avgRate}%</span>
                      </div>
                      <Progress value={cat.avgRate} indicatorColor={cat.color} className="h-2" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })()}
      </Card>

      <Card className="p-6">
        <h2 className="text-xl font-semibold text-foreground mb-6">Detalhes dos Hábitos</h2>
        <div className="space-y-6">
          {habits.map((habit) => {
            const currentStreak = getCurrentStreak(habit.id);
            const longestStreak = getLongestStreak(habit.id);
            const completionRate = getCompletionRate(habit.id);

            return (
              <div key={habit.id} className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: habit.color }}
                    />
                    <div>
                      <h3 className="font-medium text-foreground">{habit.name}</h3>
                      {habit.category && (
                        <span className="text-sm text-muted-foreground">{habit.category}</span>
                      )}
                    </div>
                  </div>
                  <span className="text-sm font-medium text-foreground">
                    {Math.round(completionRate)}%
                  </span>
                </div>


                <Progress value={completionRate} className="h-2" indicatorColor={habit.color} />

                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="flex items-center gap-1 text-muted-foreground mb-1">
                      <TrendingUp className="w-3 h-3" />
                      <span>Streak Atual</span>
                    </div>
                    <p className="font-semibold text-foreground">{currentStreak} dias</p>
                  </div>
                  <div>
                    <div className="flex items-center gap-1 text-muted-foreground mb-1">
                      <Award className="w-3 h-3" />
                      <span>Recorde</span>
                    </div>
                    <p className="font-semibold text-foreground">{longestStreak} dias</p>
                  </div>
                  <div>
                    <div className="flex items-center gap-1 text-muted-foreground mb-1">
                      <Target className="w-3 h-3" />
                      <span>Total Dias</span>
                    </div>

                    <p className="font-semibold text-foreground">
                      {completions.filter(c => {
                        if (c.habitId !== habit.id) return false;
                        if (timeRange === 'month') {
                          const today = new Date();
                          const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
                          return new Date(c.date) >= firstDayOfMonth;
                        }
                        return true;
                      }).length} dias
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <div className="pt-4">
        <InsightsCard habits={habits} completions={completions} />
      </div>

      <Dialog open={isAiOpen} onOpenChange={setIsAiOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bot className="w-6 h-6 text-purple-600" />
              Insights do seu Coach IA
            </DialogTitle>
            <DialogDescription>
              Análise personalizada baseada nos seus dados de check-in.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 min-h-[200px] flex items-center justify-center bg-muted/30 rounded-lg p-6 border border-border/50">
            {isAiLoading ? (
              <div className="flex flex-col items-center gap-3 text-muted-foreground animate-pulse">
                <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
                <p className="text-sm">Analisando seus hábitos...</p>
              </div>
            ) : (
              <div className="text-sm leading-relaxed whitespace-pre-wrap text-foreground w-full">
                {aiInsights}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div >
  );
};

export default StatsView;
